/**
 * MedicalSite
 * ───────────
 * Multi-page professional template for any niche=="Medical" entity.
 * Pages: Home (hero + lead + doctors), About (story/services), Contact,
 * Book (opens MedicalBookingModal).
 *
 * The whole site is themed by the entity's `medical_template` CSS variables
 * (primary / accent / ink / surface) — every sub-niche gets a unique
 * palette while sharing the same layout shell.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Link,
  NavLink,
  Outlet,
  Route,
  Routes,
  useNavigate,
  useOutletContext,
  useParams,
  useLocation,
} from "react-router-dom";
import { useWhitelabelSlug } from "@/pages/SiteRouter";
import {
  Calendar,
  Clock,
  Globe,
  Mail,
  MapPin,
  Phone,
  Stethoscope,
  ShieldCheck,
  Heart,
  Award,
  ChevronRight,
  ChevronLeft,
  Star,
  Facebook,
  Instagram,
  CheckCircle2,
  Menu as MenuIcon,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import MedicalBookingModal from "@/components/MedicalBookingModal";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
// ─── Sub-niche specific hero images (curated medical/Unsplash) ──────────────
const HERO_BY_SUBNICHE = {
  Hospital:          "https://images.unsplash.com/photo-1586773860418-d37222d8fce3?w=1600&q=80",
  GP:                "https://images.unsplash.com/photo-1631815589968-fdb09a223b1e?w=1600&q=80",
  Dentist:           "https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=1600&q=80",
  Pharmacy:          "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?w=1600&q=80",
  Chiropractor:      "https://images.unsplash.com/photo-1597764690472-ec054f1be9b1?w=1600&q=80",
  Optometrist:       "https://images.unsplash.com/photo-1577401239170-897942555fb3?w=1600&q=80",
  "Mental Health":   "https://images.unsplash.com/photo-1573497019418-b400bb3ab074?w=1600&q=80",
  "Sports Medicine": "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=1600&q=80",
  Podiatrist:        "https://images.unsplash.com/photo-1559757175-5700dde675bc?w=1600&q=80",
  Paediatric:        "https://images.unsplash.com/photo-1581594693702-fbdc51b2763b?w=1600&q=80",
  Maternity:         "https://images.unsplash.com/photo-1583912267550-d6e9c1e6f6c5?w=1600&q=80",
  Clinic:            "https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=1600&q=80",
};

const SERVICES_BY_SUBNICHE = {
  Hospital: ["24/7 Emergency", "Inpatient Care", "Surgery", "Imaging & Diagnostics", "Maternity Ward", "Specialist Consults"],
  GP: ["General consults", "Vaccinations & travel jabs", "Skin checks", "Women's & men's health", "Chronic disease management", "Mental health plans"],
  Dentist: ["Check-up & clean", "Fillings & crowns", "Teeth whitening", "Root canal", "Wisdom teeth", "Invisalign®"],
  Pharmacy: ["Script dispensing", "Flu & COVID vaccinations", "Medicine reviews", "Sleep apnoea consults", "Compounding", "Travel health"],
  Chiropractor: ["Spinal adjustments", "Sports chiropractic", "Posture correction", "Headache & migraine", "Sciatica relief", "Paediatric care"],
  Optometrist: ["Comprehensive eye test", "Glasses fitting", "Contact lenses", "Children's vision", "Glaucoma screening", "Dry eye therapy"],
  "Mental Health": ["Cognitive Behaviour Therapy", "Anxiety & depression", "Couples counselling", "Adolescent mental health", "Workplace wellbeing", "Telehealth sessions"],
  "Sports Medicine": ["Injury assessment", "Physiotherapy", "Sports rehab", "Pre-season screening", "Performance optimisation", "Concussion management"],
  Podiatrist: ["Nail surgery", "Orthotics", "Diabetic foot care", "Heel pain", "Sports podiatry", "Children's feet"],
  Paediatric: ["Newborn checks", "Developmental assessments", "Adolescent health", "Behavioural concerns", "Allergies & asthma", "Vaccinations"],
  Maternity: ["Antenatal care", "Birth planning", "Midwifery support", "Postnatal recovery", "Breastfeeding clinic", "Lactation consultancy"],
  Clinic: ["General practice", "Allied health", "Skin clinic", "Travel health", "Vaccinations", "Wellness assessments"],
};

// ─── Theme hook + helper ────────────────────────────────────────────────────
function useMedicalEntity(slug) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/directory/restaurants/${slug}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r.status)))
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);
  return { data, loading };
}

function buildThemeStyle(tpl) {
  return {
    "--med-primary": tpl?.primary || "#0369a1",
    "--med-accent":  tpl?.accent  || "#06b6d4",
    "--med-ink":     tpl?.ink     || "#0c4a6e",
    "--med-surface": tpl?.surface || "#f0f9ff",
  };
}

// ─── Site Header ───────────────────────────────────────────────────────────
function SiteHeader({ entity, slug, onBookClick }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const tpl = entity?.medical_template || {};
  return (
    <header
      className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b"
      style={{ borderColor: tpl.surface }}
    >
      <div className="max-w-6xl mx-auto px-4 lg:px-6 h-16 flex items-center justify-between gap-4">
        <Link to={`/r/${slug}`} className="flex items-center gap-2.5 min-w-0">
          <div
            className="w-9 h-9 rounded-xl grid place-items-center text-white text-xl shrink-0"
            style={{ background: `var(--med-primary)` }}
          >
            {tpl.icon || "⚕️"}
          </div>
          <div className="min-w-0">
            <p className="font-bold text-[15px] truncate leading-tight" style={{ color: "var(--med-ink)" }}>
              {entity?.name}
            </p>
            <p className="text-[10px] uppercase tracking-wider text-gray-500">
              {entity?.sub_niche} · {entity?.suburb || "SE QLD"}
            </p>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1 text-[14px]">
          {[
            { to: `/r/${slug}`, label: "Home" },
            { to: `/r/${slug}/services`, label: "Services" },
            { to: `/r/${slug}/doctors`, label: "Our doctors" },
            { to: `/r/${slug}/contact`, label: "Contact" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === `/r/${slug}`}
              data-testid={`med-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-lg font-medium transition-colors ${
                  isActive ? "text-white" : "text-gray-700 hover:bg-gray-100"
                }`
              }
              style={({ isActive }) => isActive ? { background: "var(--med-primary)" } : {}}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            data-testid="med-header-book"
            onClick={onBookClick}
            className="text-white rounded-full h-10 px-5 text-[13px] font-semibold shrink-0"
            style={{ background: "var(--med-primary)" }}
          >
            <Calendar className="w-4 h-4 mr-1.5" /> {tpl.cta || "Book appointment"}
          </Button>
          <button
            className="md:hidden p-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Menu"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {mobileOpen && (
        <div className="md:hidden border-t bg-white px-4 py-3 space-y-1">
          {[
            { to: `/r/${slug}`, label: "Home" },
            { to: `/r/${slug}/services`, label: "Services" },
            { to: `/r/${slug}/doctors`, label: "Our doctors" },
            { to: `/r/${slug}/contact`, label: "Contact" },
          ].map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === `/r/${slug}`}
              onClick={() => setMobileOpen(false)}
              className="block px-3 py-2 rounded-lg text-[14px] font-medium text-gray-700 hover:bg-gray-100"
            >
              {l.label}
            </NavLink>
          ))}
        </div>
      )}
    </header>
  );
}

// ─── Home page ─────────────────────────────────────────────────────────────
function MedicalHome({ entity, onBookClick }) {
  const tpl = entity.medical_template || {};
  const hero = HERO_BY_SUBNICHE[entity.sub_niche] || HERO_BY_SUBNICHE.Clinic;
  const services = SERVICES_BY_SUBNICHE[entity.sub_niche] || SERVICES_BY_SUBNICHE.Clinic;
  const doctors = (entity.doctors || []).filter((d) => !d.is_walk_in).slice(0, 3);

  return (
    <div data-testid="medical-home">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: tpl.surface }}>
        <div className="absolute inset-0 opacity-30">
          <img src={hero} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${tpl.surface} 30%, transparent 100%)`,
        }} />
        <div className="relative max-w-6xl mx-auto px-4 lg:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Badge className="text-[11px] uppercase tracking-[0.18em] font-bold border-0"
              style={{ background: "white", color: "var(--med-primary)" }}>
              {tpl.icon} {entity.sub_niche}
            </Badge>
            <h1 className="font-bold text-4xl lg:text-6xl leading-tight mt-4" style={{ color: "var(--med-ink)" }}>
              {entity.name}
            </h1>
            <p className="text-lg lg:text-xl mt-4 leading-relaxed" style={{ color: "var(--med-ink)" }}>
              {tpl.lead}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                data-testid="medical-hero-book"
                onClick={onBookClick}
                className="text-white rounded-full h-12 px-7 text-[14px] font-semibold"
                style={{ background: "var(--med-primary)" }}
              >
                <Calendar className="w-4 h-4 mr-2" /> {tpl.cta || "Book appointment"}
              </Button>
              {entity.phone && (
                <a
                  href={`tel:${entity.phone}`}
                  data-testid="medical-hero-call"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-white border-2 font-semibold text-[14px]"
                  style={{ borderColor: "var(--med-primary)", color: "var(--med-primary)" }}
                >
                  <Phone className="w-4 h-4" /> {entity.phone}
                </a>
              )}
            </div>
            {/* Trust strip */}
            <div className="mt-8 grid grid-cols-3 gap-3 text-[12px]">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4" style={{ color: "var(--med-primary)" }} />
                <span style={{ color: "var(--med-ink)" }}>Bulk-billing eligible</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4" style={{ color: "var(--med-primary)" }} />
                <span style={{ color: "var(--med-ink)" }}>Accredited practice</span>
              </div>
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4" style={{ color: "var(--med-primary)" }} />
                <span style={{ color: "var(--med-ink)" }}>{(entity.review_count || 0) + 24}+ patients</span>
              </div>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <img
              src={hero}
              alt={entity.name}
              className="w-full aspect-[4/5] object-cover rounded-3xl shadow-2xl"
            />
            <div
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl shadow-xl p-5 w-[220px]"
              style={{ borderTop: `4px solid var(--med-primary)` }}
            >
              <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Next available</p>
              <p className="text-2xl font-bold mt-1" style={{ color: "var(--med-ink)" }}>Today</p>
              <p className="text-[12px] text-gray-600 mt-1">Walk-Ins welcome</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-16">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--med-primary)" }}>
            What we do
          </p>
          <h2 className="text-3xl lg:text-4xl font-bold mt-2" style={{ color: "var(--med-ink)" }}>
            Services we offer
          </h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="medical-services-grid">
          {services.map((s) => (
            <div
              key={s}
              className="p-5 rounded-2xl border bg-white hover:shadow-lg transition-shadow"
              style={{ borderColor: tpl.surface }}
            >
              <div
                className="w-10 h-10 rounded-xl grid place-items-center mb-3"
                style={{ background: tpl.surface }}
              >
                <CheckCircle2 className="w-5 h-5" style={{ color: "var(--med-primary)" }} />
              </div>
              <p className="font-semibold text-[15px]" style={{ color: "var(--med-ink)" }}>{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Doctors strip */}
      {doctors.length > 0 && (
        <section className="py-16" style={{ background: tpl.surface }}>
          <div className="max-w-6xl mx-auto px-4 lg:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--med-primary)" }}>
                  Our team
                </p>
                <h2 className="text-3xl lg:text-4xl font-bold mt-2" style={{ color: "var(--med-ink)" }}>
                  Meet your doctors
                </h2>
              </div>
              <Link to={`/r/${entity.slug}/doctors`} className="hidden sm:flex items-center gap-1 text-[14px] font-semibold" style={{ color: "var(--med-primary)" }}>
                See all <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid sm:grid-cols-3 gap-4" data-testid="medical-doctors-grid">
              {doctors.map((d) => (
                <div key={d.id} className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <div
                    className="w-20 h-20 rounded-full mx-auto grid place-items-center text-white text-2xl font-bold mb-3"
                    style={{ background: "var(--med-primary)" }}
                  >
                    {d.name.match(/Dr\s+(\w)/)?.[1] || "D"}
                  </div>
                  <p className="font-bold text-[16px]" style={{ color: "var(--med-ink)" }}>{d.name}</p>
                  <p className="text-[12px] text-gray-600 mt-1">{d.specialty}</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-3 h-3 fill-current" style={{ color: "var(--med-accent)" }} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA strip */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 lg:px-6 text-center">
          <h3 className="text-2xl lg:text-3xl font-bold" style={{ color: "var(--med-ink)" }}>
            Book your appointment in under 60 seconds
          </h3>
          <p className="text-gray-600 mt-2">
            Pick a doctor, pick a time, fill 4 fields — done.
          </p>
          <Button
            data-testid="medical-cta-book"
            onClick={onBookClick}
            className="mt-6 text-white rounded-full h-14 px-10 text-[15px] font-semibold"
            style={{ background: "var(--med-primary)" }}
          >
            <Calendar className="w-5 h-5 mr-2" /> {tpl.cta || "Book an appointment"}
          </Button>
        </div>
      </section>
    </div>
  );
}

// ─── Services page ─────────────────────────────────────────────────────────
function MedicalServices({ entity }) {
  const services = SERVICES_BY_SUBNICHE[entity.sub_niche] || SERVICES_BY_SUBNICHE.Clinic;
  return (
    <div data-testid="medical-services-page" className="max-w-6xl mx-auto px-4 lg:px-6 py-12">
      <Link to={`/r/${entity.slug}`} className="text-[13px] flex items-center gap-1 mb-4" style={{ color: "var(--med-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to home
      </Link>
      <h1 className="text-4xl font-bold mb-3" style={{ color: "var(--med-ink)" }}>Our services</h1>
      <p className="text-gray-600 mb-8 text-lg">{entity.medical_template?.lead}</p>
      <div className="grid md:grid-cols-2 gap-4">
        {services.map((s) => (
          <div key={s} className="p-6 rounded-2xl bg-white border" style={{ borderColor: entity.medical_template?.surface }}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl grid place-items-center shrink-0" style={{ background: entity.medical_template?.surface }}>
                <Stethoscope className="w-5 h-5" style={{ color: "var(--med-primary)" }} />
              </div>
              <div>
                <p className="font-bold text-lg" style={{ color: "var(--med-ink)" }}>{s}</p>
                <p className="text-[13px] text-gray-600 mt-1">Bulk billing eligible · same-day appointments available · all ages welcome.</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Doctors page ──────────────────────────────────────────────────────────
function MedicalDoctors({ entity, onBookClick }) {
  const doctors = (entity.doctors || []).filter((d) => !d.is_walk_in);
  const walkIn = (entity.doctors || []).find((d) => d.is_walk_in);
  return (
    <div data-testid="medical-doctors-page" className="max-w-6xl mx-auto px-4 lg:px-6 py-12">
      <Link to={`/r/${entity.slug}`} className="text-[13px] flex items-center gap-1 mb-4" style={{ color: "var(--med-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to home
      </Link>
      <h1 className="text-4xl font-bold mb-3" style={{ color: "var(--med-ink)" }}>Our doctors</h1>
      <p className="text-gray-600 mb-8 text-lg">
        Choose a specific doctor or walk-in and we'll match you with the next available.
      </p>
      {walkIn && (
        <div
          className="rounded-2xl p-6 mb-6 flex items-center gap-4"
          style={{ background: entity.medical_template?.surface }}
        >
          <div className="w-14 h-14 rounded-full grid place-items-center text-white font-bold bg-slate-700">WI</div>
          <div className="flex-1">
            <p className="font-bold text-lg" style={{ color: "var(--med-ink)" }}>{walkIn.name}</p>
            <p className="text-[13px] text-gray-700">Match me with the next available doctor.</p>
          </div>
          <Button
            data-testid="med-doctor-book-walk-in"
            onClick={onBookClick}
            className="text-white rounded-full"
            style={{ background: "var(--med-primary)" }}
          >
            Book Walk-In
          </Button>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        {doctors.map((d) => (
          <div key={d.id} data-testid={`med-doctor-card-${d.id}`} className="bg-white rounded-2xl p-6 border shadow-sm">
            <div className="flex items-start gap-4">
              <div
                className="w-16 h-16 rounded-full grid place-items-center text-white text-xl font-bold shrink-0"
                style={{ background: "var(--med-primary)" }}
              >
                {d.name.match(/Dr\s+(\w)/)?.[1] || "D"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-lg" style={{ color: "var(--med-ink)" }}>{d.name}</p>
                <p className="text-[13px] text-gray-600">{d.specialty}</p>
                <div className="flex items-center gap-1 mt-1.5">
                  {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="w-3 h-3 fill-current" style={{ color: "var(--med-accent)" }} />)}
                  <span className="text-[11px] text-gray-500 ml-1">{120 + (d.name.length * 7)} reviews</span>
                </div>
              </div>
            </div>
            <Button
              onClick={onBookClick}
              variant="outline"
              className="w-full mt-4 rounded-full"
              style={{ borderColor: "var(--med-primary)", color: "var(--med-primary)" }}
              data-testid={`med-doctor-book-${d.id}`}
            >
              Book with {d.name}
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Contact page ──────────────────────────────────────────────────────────
function MedicalContact({ entity }) {
  return (
    <div data-testid="medical-contact-page" className="max-w-6xl mx-auto px-4 lg:px-6 py-12">
      <Link to={`/r/${entity.slug}`} className="text-[13px] flex items-center gap-1 mb-4" style={{ color: "var(--med-primary)" }}>
        <ChevronLeft className="w-4 h-4" /> Back to home
      </Link>
      <h1 className="text-4xl font-bold mb-3" style={{ color: "var(--med-ink)" }}>Visit or contact us</h1>
      <div className="grid md:grid-cols-2 gap-6 mt-8">
        <div className="rounded-2xl p-6 border bg-white space-y-4" style={{ borderColor: entity.medical_template?.surface }}>
          {entity.address && (
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--med-primary)" }} />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Address</p>
                <p className="text-[15px]" style={{ color: "var(--med-ink)" }}>{entity.address}</p>
                <p className="text-[13px] text-gray-600">{entity.suburb} {entity.postcode || ""}</p>
              </div>
            </div>
          )}
          {entity.phone && (
            <div className="flex items-start gap-3">
              <Phone className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--med-primary)" }} />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Phone</p>
                <a href={`tel:${entity.phone}`} className="text-[15px] hover:underline" style={{ color: "var(--med-ink)" }}>{entity.phone}</a>
              </div>
            </div>
          )}
          {entity.email && (
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--med-primary)" }} />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Email</p>
                <a href={`mailto:${entity.email}`} className="text-[15px] hover:underline" style={{ color: "var(--med-ink)" }}>{entity.email}</a>
              </div>
            </div>
          )}
          {entity.website && (
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--med-primary)" }} />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Website</p>
                <a href={entity.website} target="_blank" rel="noopener noreferrer" className="text-[15px] hover:underline" style={{ color: "var(--med-ink)" }}>
                  {entity.website.replace(/^https?:\/\//, "")}
                </a>
              </div>
            </div>
          )}
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 shrink-0 mt-0.5" style={{ color: "var(--med-primary)" }} />
            <div>
              <p className="text-[11px] uppercase tracking-wider text-gray-500 font-bold">Hours</p>
              <p className="text-[15px]" style={{ color: "var(--med-ink)" }}>Mon–Fri 9:00am — 5:00pm</p>
              <p className="text-[13px] text-gray-600">Closed weekends &amp; public holidays</p>
            </div>
          </div>
          {(entity.facebook || entity.instagram) && (
            <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: entity.medical_template?.surface }}>
              {entity.facebook && (
                <a href={entity.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center" style={{ background: entity.medical_template?.surface }}>
                  <Facebook className="w-4 h-4" style={{ color: "var(--med-primary)" }} />
                </a>
              )}
              {entity.instagram && (
                <a href={entity.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full grid place-items-center" style={{ background: entity.medical_template?.surface }}>
                  <Instagram className="w-4 h-4" style={{ color: "var(--med-primary)" }} />
                </a>
              )}
            </div>
          )}
        </div>
        <div className="rounded-2xl overflow-hidden border" style={{ borderColor: entity.medical_template?.surface, minHeight: 360 }}>
          {entity.lat && entity.lng ? (
            <iframe
              title="Practice location"
              width="100%"
              height="100%"
              style={{ minHeight: 360, border: 0 }}
              loading="lazy"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${entity.lng - 0.005}%2C${entity.lat - 0.003}%2C${entity.lng + 0.005}%2C${entity.lat + 0.003}&layer=mapnik&marker=${entity.lat}%2C${entity.lng}`}
            />
          ) : (
            <div className="h-full grid place-items-center text-gray-500 p-12">Map unavailable</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Site Layout (shared) ──────────────────────────────────────────────────
function MedicalSiteLayout() {
  // iter373c patch F — whitelabel host mode renders this layout at "/"
  // with no `:slug` param, so fall back to the SiteRouter-published slug.
  const params = useParams();
  const forced = useWhitelabelSlug();
  const slug = params.slug || forced;
  const navigate = useNavigate();
  const loc = useLocation();
  const { data, loading } = useMedicalEntity(slug);
  const [bookOpen, setBookOpen] = useState(false);
  // iter373c patch G — whitelabel mode strips the "Powered by HustlePlate"
  // footer line so the vendor's site looks fully self-branded.
  const isWhitelabel = !!forced || !!(data && data.hide_hustle_header);

  // Auto-redirect to /directory if this isn't a medical entity (the SiteRouter
  // wrapper should usually catch this first, but belt-and-braces).
  useEffect(() => {
    if (!loading && data && data.niche && data.niche !== "Medical") {
      navigate(`/r/${slug}${loc.pathname.replace(`/r/${slug}`, "")}`, { replace: true });
    }
  }, [loading, data, navigate, slug, loc.pathname]);

  // ── SEO: inject schema.org JSON-LD into <head> for Google rich results
  useEffect(() => {
    if (!data?.slug) return;
    const scriptId = "medical-jsonld";
    fetch(`${API}/api/directory/restaurants/${data.slug}/schema`)
      .then((r) => r.json())
      .then((schema) => {
        let el = document.getElementById(scriptId);
        if (!el) {
          el = document.createElement("script");
          el.type = "application/ld+json";
          el.id = scriptId;
          document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(schema);
      })
      .catch(() => {});
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [data?.slug]);

  if (loading) {
    return <div className="min-h-[60vh] grid place-items-center text-gray-500">Loading practice…</div>;
  }
  if (!data) {
    return <div className="min-h-[60vh] grid place-items-center text-gray-500">Practice not found.</div>;
  }

  const style = buildThemeStyle(data.medical_template);

  return (
    <div className="min-h-screen bg-white" style={style}>
      <SiteHeader entity={data} slug={slug} onBookClick={() => setBookOpen(true)} />
      <Outlet context={{ entity: data, onBookClick: () => setBookOpen(true) }} />
      {/* Footer */}
      <footer className="border-t mt-12 py-8" style={{ borderColor: data.medical_template?.surface, background: data.medical_template?.surface }}>
        <div className="max-w-6xl mx-auto px-4 lg:px-6 text-center text-[12px] text-gray-600">
          <p>© {new Date().getFullYear()} {data.name}{isWhitelabel ? "" : " · Powered by HustlePlate"}</p>
        </div>
      </footer>

      {/* Floating book CTA on mobile */}
      <button
        data-testid="medical-floating-book"
        onClick={() => setBookOpen(true)}
        className="md:hidden fixed bottom-4 right-4 z-40 px-5 h-12 rounded-full shadow-xl text-white text-[14px] font-semibold flex items-center gap-2"
        style={{ background: "var(--med-primary)" }}
      >
        <Calendar className="w-4 h-4" /> Book
      </button>

      <MedicalBookingModal
        practice={data}
        open={bookOpen}
        onClose={() => setBookOpen(false)}
      />
    </div>
  );
}

// ─── Outlet consumers ──────────────────────────────────────────────────────
function OutletConsumer({ Page }) {
  const { entity, onBookClick } = useOutletContext();
  return <Page entity={entity} onBookClick={onBookClick} />;
}

export default function MedicalSite() {
  return (
    <Routes>
      <Route element={<MedicalSiteLayout />}>
        <Route index element={<OutletConsumer Page={MedicalHome} />} />
        <Route path="services" element={<OutletConsumer Page={MedicalServices} />} />
        <Route path="doctors" element={<OutletConsumer Page={MedicalDoctors} />} />
        <Route path="contact" element={<OutletConsumer Page={MedicalContact} />} />
        <Route path="*" element={<OutletConsumer Page={MedicalHome} />} />
      </Route>
    </Routes>
  );
}
